<template>
  <div class="candidate-tabs-panel fill-height pr-md-3">
    <VTabs
      v-model="activeTabIndex"
      background-color="transparent"
      color="dark"
      slider-size="4"
      slider-color="primary"
      :grow="$vuetify.breakpoint.smAndDown"
      :show-arrows="false"
      class="candidate-tabs-panel__tabs fill-height pt-md-4"
    >
      <VTab
        class="mr-md-6 mr-2 ml-1 ml-md-12"
        :class="{ title: $vuetify.breakpoint.mdAndUp, 'subtitle-2': $vuetify.breakpoint.smAndDown }"
      >
        {{ $t('events') }}
      </VTab>
      <VTab
        v-if="showQuestionsTab"
        class="mr-md-6 mr-2"
        :class="{ title: $vuetify.breakpoint.mdAndUp, 'subtitle-2': $vuetify.breakpoint.smAndDown }"
      >
        {{ $t('questions') }}
      </VTab>
      <VTab
        v-if="!readonly && !candidate.selfRefused"
        class="mr-md-6 mr-2"
        :class="{ title: $vuetify.breakpoint.mdAndUp, 'subtitle-2': $vuetify.breakpoint.smAndDown }"
      >
        {{ $t('connectWithCandidate') }}
      </VTab>
      <VTab
        v-if="candidate.testFinishedAt"
        class="mr-md-6 mr-2"
        :class="{ title: $vuetify.breakpoint.mdAndUp, 'subtitle-2': $vuetify.breakpoint.smAndDown }"
      >
        {{ $t('testing') }}
      </VTab>
      <VTabItem>
        <CandidateLog
          :candidate="candidate"
          :readonly="readonly"
          :vacancy="vacancy"
          :online-interview="candidate.interview"
          :show-review="showReview"
          :pdf-resume-loading="pdfResumeLoading"
          @show-interview="$emit('show-interview', $event)"
          @show-online-interview="$emit('show-online-interview')"
          @change-active-tab="changeActiveTab($event)"
          @generate-candidate-pdf="$emit('generate-candidate-pdf')"
        />
      </VTabItem>
      <VTabItem v-if="showQuestionsTab">
        <VacancyQuestionsList
          ref="vacancyQuestionsList"
          v-if="candidate.interview"
          v-model="activeQuestion"
          :candidate="candidate"
          :vacancy="vacancy"
          :online-interview="candidate.interview"
          :are-questions-equal="areQuestionsEqual"
          @change-active-tab="changeActiveTab($event)"
        />
      </VTabItem>
      <VTabItem v-if="!readonly || candidate.permissions.notifiable">
        <CandidateContact :candidate="candidate" />
      </VTabItem>
      <VTabItem v-if="candidate.testFinishedAt">
        <CandidateTestResult
          :candidate="candidate"
        />
      </VTabItem>
    </VTabs>
  </div>
</template>

<script>
import { candidateStatus } from '@/enums';
import CandidateLog from './CandidateLog.vue';
import CandidateContact from './CandidateContact.vue';
import VacancyQuestionsList from '../Vacancy/VacancyQuestionsList.vue';
import CandidateTestResult from './CandidateTestResult.vue';

export default {
  name: 'CandidateTabsPanel',
  components: {
    CandidateContact,
    VacancyQuestionsList,
    CandidateLog,
    CandidateTestResult,
  },
  model: {
    prop: 'activeTab',
    event: 'input',
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    activeTab: {
      type: Number,
      default: 0,
    },
    candidate: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showQuestionsTab: {
      type: Boolean,
      default: false,
    },
    question: {
      type: Number,
      default: 0,
    },
    pdfResumeLoading: {
      type: Boolean,
      default: false,
    },
    areQuestionsEqual: {
      type: Boolean,
      required: true,
      default: true,
    },
  },
  data() {
    return {
      statusListToContact: [
        candidateStatus.INOFFICE_INTERVIEW,
        candidateStatus.JOB_OFFER,
        candidateStatus.DECLINED,
      ],
      showReview: false,
    };
  },
  computed: {
    activeTabIndex: {
      get() {
        return this.activeTab;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    activeQuestion: {
      get() {
        return this.question;
      },
      set(value) {
        this.$emit('updated:question', value);
      },
    },
    contactTabIndex() {
      return this.showQuestionsTab ? 2 : 1;
    },
    showContactTab() {
      return !this.readonly
        && ((this.showQuestionsTab && this.activeTabIndex === 2)
          || (!this.showQuestionsTab && this.activeTabIndex === 1)
        );
    },
  },
  watch: {
    candidate(newValue, oldValue) {
      if (
        newValue.id === oldValue.id
        && newValue.status !== oldValue.status
        && this.statusListToContact.includes(newValue.status)
      ) {
        this.activeTabIndex = this.contactTabIndex;
      }
    },
  },
  methods: {
    changeActiveTab(tabId) {
      if (tabId === 'contact') {
        this.activeTabIndex = this.contactTabIndex;
      } else {
        this.activeTabIndex = tabId;
        this.showReview = !this.showReview;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.candidate-tabs-panel {
  &::v-deep * {
    box-sizing: border-box;
  }
}

.candidate-tabs-panel__tabs {
  &::v-deep {
    .v-tabs-bar--is-mobile {
      .v-slide-group__prev, .v-slide-group__next {
        display: none !important;
      }
    }

    .v-tabs-bar {
      margin-bottom: 0;
    }

    .v-tabs-items {
      height: calc(100% - 48px);

      .v-window__container {
        height: 100% !important;

        .v-window-item {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          overflow: hidden;
        }
      }

    }
  }
}

.v-tab::v-deep {
  font-size: 14px !important;
  @media screen and (max-width: 428px) {
    font-size: 12px !important;
  }
  @media screen and (max-width: 360px) {
    font-size: 10px !important;
  }
}
</style>

<i18n locale="en" lang="yaml">
  events: Events
  questions: Questions
  connectWithCandidate: Send candidate a message
  testing: Testing
</i18n>

<i18n locale="ru" lang="yaml">
  events: События
  questions: Вопросы
  connectWithCandidate: Связь с кандидатом
  testing: Тестирование
</i18n>
